import { useState } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { Phone, Mail, Menu, X, ShieldCheck, MapPin, ChevronRight } from "lucide-react";
import { CITIES, SERVICES, COUNTIES, PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/data/contentEngine";

const LogoMark = () => (
  <svg viewBox="0 0 40 40" className="h-9 w-9 shrink-0" data-testid="logo-mark" aria-hidden="true">
    <rect x="1" y="1" width="38" height="38" rx="8" fill="#0D111A" stroke="#F59E0B" strokeWidth="2" />
    <path d="M12 12 L28 28 M28 12 L12 28" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const navLinkClass = ({ isActive }) =>
  `font-display text-sm font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
    isActive ? "text-amber-400" : "text-slate-300 hover:text-amber-400"
  }`;

export const Marquee = () => {
  const items = [
    ...CITIES.map((c) => c.name),
    "SIA Licensed Officers",
    "BS7858 Vetted",
    "24/7 Control Room",
    "Rapid Alarm Response",
    "Northern Ireland Wide",
  ];
  const row = [...items, ...items];
  return (
    <div
      className="relative overflow-hidden border-y border-amber-500/20 bg-obsidian-900 py-4"
      data-testid="coverage-marquee"
      aria-hidden="true"
    >
      <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
        {[...row, ...row].map((t, i) => (
          <span
            key={i}
            className="font-display text-sm font-semibold uppercase tracking-[0.3em] text-slate-400"
          >
            {t} <span className="ml-6 text-amber-500">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="fixed inset-x-0 top-0 z-40 border-b border-slate-800/80 bg-obsidian-950/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
          <LogoMark />
          <span className="leading-none">
            <span className="block font-display text-lg font-bold uppercase tracking-[0.12em] text-slate-100">
              Xtreme Protection
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              Northern Ireland Security Services
            </span>
          </span>
        </Link>
        <nav className="hidden items-center gap-8 md:flex" data-testid="primary-nav">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home-link">
            Home
          </NavLink>
          <NavLink to="/services" className={navLinkClass} data-testid="nav-services-link">
            Services
          </NavLink>
          <NavLink to="/cities" className={navLinkClass} data-testid="nav-cities-link">
            Areas We Cover
          </NavLink>
          <NavLink to="/insights" className={navLinkClass} data-testid="nav-insights-link">
            Insights
          </NavLink>
          <a
            href={PHONE_HREF}
            data-testid="header-call-button"
            className="flex items-center gap-2 rounded-sm bg-amber-500 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian-950 transition-all duration-300 hover:bg-amber-400 active:scale-95"
          >
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </nav>
        <button
          className="text-slate-200 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open ? (
        <nav
          className="border-t border-slate-800 bg-obsidian-950 px-4 py-4 md:hidden"
          data-testid="mobile-nav"
        >
          {[
            { to: "/", label: "Home" },
            { to: "/services", label: "Services" },
            { to: "/cities", label: "Areas We Cover" },
            { to: "/insights", label: "Insights" },
            { to: "/careers", label: "Careers" },
            { to: "/sitemap", label: "Site Directory" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className="block py-3 font-display text-base font-semibold uppercase tracking-[0.18em] text-slate-200"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      ) : null}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-slate-800 bg-obsidian-950" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <LogoMark />
            <span className="font-display text-lg font-bold uppercase tracking-[0.12em] text-slate-100">
              Xtreme Protection
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            SIA-licensed security guards, mobile patrols, CCTV monitoring and rapid alarm
            response protecting businesses across every city and town in Northern Ireland.
          </p>
          <div className="mt-5 space-y-2 text-sm">
            <a
              href={PHONE_HREF}
              data-testid="footer-phone-link"
              className="flex items-center gap-2 text-slate-300 transition-colors hover:text-amber-400"
            >
              <Phone className="h-4 w-4 text-amber-500" /> {PHONE}
            </a>
            <a
              href={EMAIL_HREF}
              data-testid="footer-email-link"
              className="flex items-center gap-2 text-slate-300 transition-colors hover:text-amber-400"
            >
              <Mail className="h-4 w-4 text-amber-500" /> {EMAIL}
            </a>
            <p className="flex items-center gap-2 text-slate-400">
              <MapPin className="h-4 w-4 text-amber-500" /> Serving all of Northern Ireland
            </p>
          </div>
        </div>
        <nav aria-label="Security services" data-testid="footer-services-nav">
          <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
            Security Services
          </h3>
          <ul className="mt-4 space-y-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group flex items-center text-sm text-slate-400 transition-colors hover:text-amber-400"
                  data-testid={`footer-service-${s.slug}`}
                >
                  <ChevronRight className="mr-1 h-3 w-3 text-slate-600 transition-colors group-hover:text-amber-500" />
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Areas covered" data-testid="footer-cities-nav">
          <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
            Areas We Cover
          </h3>
          <ul className="mt-4 grid grid-cols-1 gap-2">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/cities/${c.slug}`}
                  className="group flex items-center text-sm text-slate-400 transition-colors hover:text-amber-400"
                  data-testid={`footer-city-${c.slug}`}
                >
                  <ChevronRight className="mr-1 h-3 w-3 text-slate-600 transition-colors group-hover:text-amber-500" />
                  Security Guards {c.name}
                </Link>
              </li>
            ))}
            {COUNTIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/counties/${c.slug}`}
                  className="group flex items-center text-sm font-semibold text-slate-300 transition-colors hover:text-amber-400"
                  data-testid={`footer-county-${c.slug}`}
                >
                  <ChevronRight className="mr-1 h-3 w-3 text-amber-500/60 transition-colors group-hover:text-amber-500" />
                  Security Company {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Company" data-testid="footer-company-nav">
          <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
            Company
          </h3>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <Link to="/" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-home-link">
                Home
              </Link>
            </li>
            <li>
              <Link to="/services" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-all-services-link">
                All Security Services
              </Link>
            </li>
            <li>
              <Link to="/cities" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-all-cities-link">
                All Areas Covered
              </Link>
            </li>
            <li>
              <Link to="/insights" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-insights-link">
                Security Insights
              </Link>
            </li>
            <li>
              <Link to="/careers" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-careers-link">
                Security Jobs NI
              </Link>
            </li>
            <li>
              <Link to="/sitemap" className="text-slate-400 transition-colors hover:text-amber-400" data-testid="footer-sitemap-link">
                Full Site Directory
              </Link>
            </li>
          </ul>
          <div className="mt-8 flex items-center gap-2 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-300">
              SIA Licensed · BS7858 Vetted
            </span>
          </div>
        </nav>
      </div>
      <div className="mt-14 border-t border-slate-800 pt-6 text-center">
        <p className="text-xs text-slate-500" data-testid="footer-copyright">
          © 2026 Xtreme Protection — Northern Ireland Security Services. Security guards,
          patrols and monitoring across Belfast, Derry / Londonderry, Lisburn, Newry, Armagh,
          Bangor and all of Northern Ireland.
        </p>
      </div>
    </div>
  </footer>
);

const StickyBar = () => (
  <div
    className="fixed inset-x-0 bottom-0 z-50 border-t border-amber-500/30 bg-obsidian-950/90 px-4 py-3 shadow-[0_-10px_30px_rgba(0,0,0,0.8)] backdrop-blur-xl"
    data-testid="sticky-contact-bar"
  >
    <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 sm:gap-6">
      <a
        href={PHONE_HREF}
        data-testid="sticky-phone-link"
        className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-amber-500 px-3 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-obsidian-950 transition-all duration-300 hover:bg-amber-400 active:scale-95 sm:flex-none sm:px-8"
      >
        <Phone className="h-4 w-4" />
        <span className="hidden xs:inline sm:inline">{PHONE}</span>
        <span className="xs:hidden sm:hidden">Call Now</span>
      </a>
      <a
        href={EMAIL_HREF}
        data-testid="sticky-email-link"
        className="flex flex-1 items-center justify-center gap-2 rounded-sm border border-amber-500/50 bg-transparent px-3 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-amber-400 transition-all duration-300 hover:bg-amber-500/10 active:scale-95 sm:flex-none sm:px-8"
      >
        <Mail className="h-4 w-4" />
        <span className="hidden md:inline">{EMAIL}</span>
        <span className="md:hidden">Email Us</span>
      </a>
    </div>
  </div>
);

export const Layout = () => (
  <div className="min-h-screen bg-obsidian-950 pb-[68px] text-slate-100">
    <Header />
    <main className="pt-16">
      <Outlet />
    </main>
    <Footer />
    <StickyBar />
  </div>
);
